import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addDepartment, editDepartment, getDepartmentById } from '../../../api/endpoints/corporate';
import type { Department } from '../../../api/types/corporate';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { useFreshEditRow } from '../../../components/useFreshEditRow';

const schema = z.object({
  name: z.string().min(1, 'Departman adı gerekli'),
  parentDepartmentId: z.coerce.number().optional(),
  isActive: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = { name: '', parentDepartmentId: undefined, isActive: true };

function toFormValues(d: Department): FormValues {
  return { name: d.name ?? '', parentDepartmentId: d.parentDepartmentId ?? undefined, isActive: d.isActive ?? true };
}

/** Create/edit dialog for Departman. `options` is the flattened, depth-indented department
 *  list used to populate the "Üst Departman" select — `excludeIds` (self + descendants when
 *  editing) keeps the picker from letting a department become its own ancestor. */
export function DepartmentFormDialog({
  open,
  department,
  options,
  excludeIds,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  department: Department | null;
  options: { id: number; label: string }[];
  excludeIds: Set<number>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!department;

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  // Refetch the fresh row on edit-open; silently falls back to the list row on error.
  const seed = useFreshEditRow({
    open,
    editing: department,
    queryKey: ['corporate', 'department', department?.id],
    fetch: async (row) => (await getDepartmentById(row.id)).departments?.[0],
  });

  useEffect(() => {
    if (open) reset(seed ? toFormValues(seed) : emptyValues);
  }, [open, seed, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && department
        ? editDepartment({ ...values, id: department.id, parentDepartmentId: values.parentDepartmentId ?? null })
        : addDepartment({ ...values, parentDepartmentId: values.parentDepartmentId ?? null }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Departman güncellendi.' : 'Departman oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporate', 'departments'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));
  const selectableOptions = options.filter((o) => !excludeIds.has(o.id));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Departmanı Güncelle' : 'Yeni Departman'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Departman Adı"
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register('name')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                control={control}
                name="parentDepartmentId"
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    select
                    fullWidth
                    size="small"
                    label="Üst Departman"
                  >
                    <MenuItem value="">Kök (üst departman yok)</MenuItem>
                    {selectableOptions.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                control={control}
                name="isActive"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Aktif"
                  />
                )}
              />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
